import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { LIVE_PREVIEW_DEBOUNCE_TIME } from '@features/invoices/constants/livePreviewDebounceTime'
import { InvoiceDiscountMode } from '@features/invoices/enums/invoiceDiscountMode'
import { InvoiceDocumentType } from '@features/invoices/enums/invoiceDocumentType'
import { useInvoicePreview } from '@features/invoices/hooks/useInvoicePreview'
import { InvoiceForm } from '@features/invoices/types/invoiceForm/invoiceForm'
import { useZervantSettings } from '@features/invoicingSettings/hooks/useZervantSettings'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { FormControl, useDebouncedValue, useWatch } from '../../../hooks'
import { DemoInvoiceFormSchema } from '../types/demoInvoiceFormSchema'
import { getDemoInvoicePayload } from '../utils/getDemoInvoicePayload'

export const useDemoInvoicePreview = (
  control: FormControl<DemoInvoiceFormSchema>,
  discountMode: InvoiceDiscountMode,
) => {
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const { data: zervantSettings, isLoading: isZervantSettingsLoading } = useZervantSettings()

  const invoiceValues = useWatch({ control }) as InvoiceForm
  const debouncedInvoiceValues = useDebouncedValue(invoiceValues, LIVE_PREVIEW_DEBOUNCE_TIME)

  const invoiceDraftPayload = useMemo(
    () =>
      isZervantSettingsLoading
        ? undefined
        : getDemoInvoicePayload({
            discountMode,
            invoiceValues: debouncedInvoiceValues,
            organization,
            t,
            zervantSettings,
          }),
    [debouncedInvoiceValues, discountMode, isZervantSettingsLoading, organization, t, zervantSettings],
  )

  const { htmlFiles, isLoading } = useInvoicePreview({ documentType: InvoiceDocumentType.Invoice, invoiceDraftPayload })

  return { htmlFiles, isLoading: isLoading || isZervantSettingsLoading }
}
